'use client';

import { useState } from 'react';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import InputAdornment from '@mui/material/InputAdornment';

import { useBoolean } from 'src/hooks/use-boolean';

import { Iconify } from 'src/components/iconify';
import { toast } from 'src/components/snackbar';

import type { IOrganisation, OrganisationsTyp, ISkapaOrganisationRequest } from 'src/types/nya/common';

import { SkapaOrganisation, VerifieraOrganisation } from './OrganisationsService';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: (nyskapad?: IOrganisation) => void;
};

const ORGANISATIONS_TYPER: { value: OrganisationsTyp; label: string }[] = [
  { value: 'BOSTADSRÄTTSFÖRENING', label: 'Bostadsrättsförening' },
  { value: 'EKONOMISK_FÖRENING', label: 'Ekonomisk förening' },
  { value: 'AKTIEBOLAG', label: 'Aktiebolag' },
  { value: 'FÖRETAG', label: 'Företag' },
  { value: 'OKÄND', label: 'Okänd' },
];

const innevarandeÅr = new Date().getFullYear();

const tomtFormulär = {
  organisationsNummer: '',
  organisationsNamn: '',
  organisationsTyp: 'BOSTADSRÄTTSFÖRENING' as OrganisationsTyp,
  momsregistreringsnummer: '',
  förstaDag: `${innevarandeÅr}-01-01`,
  sistaDag: `${innevarandeÅr}-12-31`,
};

export default function LäggTillOrganisationDialog({ open, onClose, onCreated }: Props) {
  const söker = useBoolean();
  const sparar = useBoolean();

  const [form, setForm] = useState(tomtFormulär);
  const [prefylld, setPrefylld] = useState(false);

  const uppdatera = (fält: keyof typeof form) => (värde: string) =>
    setForm((prev) => ({ ...prev, [fält]: värde }));

  const stäng = () => {
    setForm(tomtFormulär);
    setPrefylld(false);
    söker.onFalse();
    sparar.onFalse();
    onClose();
  };

  const handleSök = async () => {
    if (!form.organisationsNummer.trim()) {
      toast.error('Ange ett organisationsnummer att söka på.');
      return;
    }
    söker.onTrue();
    try {
      const org = await VerifieraOrganisation(form.organisationsNummer.trim());
      setForm((prev) => ({
        ...prev,
        organisationsNamn: org.organisationsNamn ?? prev.organisationsNamn,
        organisationsNummer: org.organisationsNummer ?? prev.organisationsNummer,
        organisationsTyp: (org.organisationsTyp as OrganisationsTyp) ?? prev.organisationsTyp,
        momsregistreringsnummer: org.momsregisteringsnummer ?? prev.momsregistreringsnummer,
      }));
      setPrefylld(true);
      toast.success('Organisationen hittades hos Bolagsverket.');
    } catch (error) {
      console.error(error);
      toast.error('Kunde inte hitta organisationen. Du kan fylla i uppgifterna manuellt.');
    } finally {
      söker.onFalse();
    }
  };

  const handleSpara = async () => {
    if (!form.organisationsNamn.trim() || !form.organisationsNummer.trim()) {
      toast.error('Namn och organisationsnummer krävs.');
      return;
    }
    sparar.onTrue();
    try {
      const request: ISkapaOrganisationRequest = {
        organisationsNummer: form.organisationsNummer.trim(),
        organisationsNamn: form.organisationsNamn.trim(),
        organisationsTyp: form.organisationsTyp,
        momsregistreringsnummer: form.momsregistreringsnummer.trim() || undefined,
        räkenskapsår:
          form.förstaDag && form.sistaDag
            ? { förstaDag: form.förstaDag, sistaDag: form.sistaDag }
            : undefined,
      };
      const nyskapad = await SkapaOrganisation(request);
      toast.success('Organisationen har lagts till.');
      onCreated(nyskapad);
      stäng();
    } catch (error) {
      console.error(error);
      toast.error('Något gick fel när organisationen skulle sparas.');
    } finally {
      sparar.onFalse();
    }
  };

  return (
    <Dialog open={open} onClose={stäng} fullWidth maxWidth="sm">
      <DialogTitle>Lägg till organisation</DialogTitle>

      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Slå upp hos Bolagsverket
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Organisationsnummer"
                placeholder="769600-0000"
                value={form.organisationsNummer}
                onChange={(e) => uppdatera('organisationsNummer')(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSök();
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                loading={söker.value}
                onClick={handleSök}
                sx={{ flexShrink: 0 }}
              >
                Sök
              </Button>
            </Stack>
          </Box>

          {prefylld && (
            <Alert severity="success" variant="outlined">
              Uppgifterna förifylldes från Bolagsverket. Kontrollera och justera vid behov.
            </Alert>
          )}

          <Divider>
            <Typography variant="caption" color="text.secondary">
              Uppgifter
            </Typography>
          </Divider>

          <TextField
            fullWidth
            required
            label="Organisationsnamn"
            value={form.organisationsNamn}
            onChange={(e) => uppdatera('organisationsNamn')(e.target.value)}
          />

          <TextField
            select
            fullWidth
            label="Typ"
            value={form.organisationsTyp}
            onChange={(e) => uppdatera('organisationsTyp')(e.target.value)}
          >
            {ORGANISATIONS_TYPER.map((typ) => (
              <MenuItem key={typ.value} value={typ.value}>
                {typ.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            label="Momsregistreringsnummer"
            placeholder="SE769600000001"
            value={form.momsregistreringsnummer}
            onChange={(e) => uppdatera('momsregistreringsnummer')(e.target.value)}
          />

          <Divider>
            <Typography variant="caption" color="text.secondary">
              Räkenskapsår
            </Typography>
          </Divider>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              type="date"
              label="Första dag"
              value={form.förstaDag}
              onChange={(e) => uppdatera('förstaDag')(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth
              type="date"
              label="Sista dag"
              value={form.sistaDag}
              onChange={(e) => uppdatera('sistaDag')(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button color="inherit" onClick={stäng}>
          Avbryt
        </Button>
        <Button variant="contained" loading={sparar.value} onClick={handleSpara}>
          Lägg till
        </Button>
      </DialogActions>
    </Dialog>
  );
}
